import React, { useEffect, useState } from 'react';
import { GameWinScenario } from './GameWinScenario';
import { GameLoseScenario } from './GameLoseScenario';
import type { PotionAttempt } from './PotionAttemptDisplay';
import { useAppStore } from '@/hooks/use-app-store';
import { expressionPatterns, vrmExpressionController } from '@/lib/vrm-expression-controller';

export interface GameEndState {
  isVisible: boolean;
  gameWon: boolean;
  potionAttempts: PotionAttempt[];
}

interface GameEndOverlayProps {
  gameEndState: GameEndState;
  onHome: () => void;
  onClose: () => void;
  className?: string;
}

export const GameEndOverlay: React.FC<GameEndOverlayProps> = ({ 
  gameEndState, 
  onHome, 
  onClose,
  className = '' 
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { triggerAvatarExpression } = useAppStore();

  const { isVisible, gameWon, potionAttempts } = gameEndState;

  // Handle entrance animation and VRM expression
  useEffect(() => {
    if (isVisible && potionAttempts.length > 0) {
      setIsTransitioning(true);
      
      // Trigger appropriate VRM expression based on win/lose
      setTimeout(() => {
        if (gameWon) {
          // Happy expression for win
          console.log('🎉 Triggering happy expression for game win');
          expressionPatterns.showHappiness(0, 1.0, 2000);
          // Alternative: triggerAvatarExpression(0, 'happy', 1.0);
        } else {
          // Relaxed then angry expressions sequence for lose  
          console.log('� Triggering relaxed → angry expression sequence for game lose');
          // First: relaxed expression
          vrmExpressionController.setExpression(0, 'relaxed', 1.0, 300);
          // Then: angry expression after a short delay
          setTimeout(() => {
            vrmExpressionController.setExpression(0, 'angry', 1.0, 300);
          }, 350); // Small delay to let relaxed finish
        }
      }, 1000);

      // Show content after transition
      setTimeout(() => {
        setShowContent(true);
        setIsTransitioning(false);
      }, 2000);
    } else if (!isVisible) {
      setShowContent(false);
      setIsTransitioning(false);
      
      // Reset VRM to neutral when hiding
      setTimeout(() => {
        expressionPatterns.resetToNeutral(0, 1000);
      }, 500);
    }
  }, [isVisible, gameWon, potionAttempts.length, triggerAvatarExpression]);

  // Handle home action with cleanup
  const handleHome = () => {
    console.log('🏠 GameEndOverlay: Handling home action');
    setShowContent(false);
    setIsTransitioning(false);
    
    // Reset VRM expression
    expressionPatterns.resetToNeutral(0, 500);
    
    // Call parent handlers
    onHome();
    onClose();
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Transition Effect */}
      {isTransitioning && (
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 opacity-60 animate-pulse" />
      )}
      
      {/* Content */}
      <div 
        className={`relative w-full h-full transition-all duration-1000 ${
          showContent 
            ? 'opacity-100 scale-100' 
            : 'opacity-0 scale-95'
        }`}
      >
        {showContent && (
          <>
            {gameWon ? (
              <GameWinScenario 
                potionAttempts={potionAttempts} 
                onHome={handleHome}
              />
            ) : (
              <GameLoseScenario 
                potionAttempts={potionAttempts} 
                onHome={handleHome}
              />
            )}
          </>
        )}
      </div>
      
      {/* Transition Loading */}
      {isTransitioning && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="text-6xl animate-spin">
              {gameWon ? '✨' : '💫'}
            </div>
            <div className="text-2xl text-white font-bold animate-pulse">
              {gameWon ? 'Celebrating Victory...' : 'Assessing Results...'}
            </div>
          </div>
        </div>
      )}
      
      {/* Debug Info (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 left-4 bg-black/60 text-white p-2 rounded text-xs">
          <div>Game Won: {gameWon ? 'Yes' : 'No'}</div>
          <div>Attempts: {potionAttempts.length}</div>
          <div>Transitioning: {isTransitioning ? 'Yes' : 'No'}</div>
          <div>Show Content: {showContent ? 'Yes' : 'No'}</div>
        </div>
      )}
    </div>
  );
};